import { sorted } from "helpers";
import { MixBasket } from "src/store/api/generatedApi";

export interface DisplayLayerComponent {
  materialName: string;
  mass: number;
}

export interface DisplayLayer {
  layerNumber: number;
  materialName: string;
  mass: number;
  components: DisplayLayerComponent[];
}

export interface BasketLayeringComparison {
  basketNumber: number;
  planned: DisplayLayer[];
  reported: DisplayLayer[];
  measured: DisplayLayer[];
  scaleMass: number;
}

// The planned column is the reference: its layers should fill this fraction of the
// fixed bucket height, and the same mass-to-height scale is applied to the reported
// and measured columns so all three are directly comparable.
export const PLANNED_FILL_FRACTION = 0.8;

const totalMass = (layers: DisplayLayer[]): number =>
  layers.reduce((sum, layer) => sum + layer.mass, 0);

interface GroupingAccumulator {
  layers: DisplayLayer[];
  lastGroupId: number | null;
}

// Adjacent layers sharing a layer_group_id are the sub-layers of one dual-material
// deposit: they fold into a single display layer whose components drive the striped
// rendering. Layers without a group id (non-ScrapEye sources, older data) stay solo.
const fromMixLayers = (basket: MixBasket | undefined): DisplayLayer[] =>
  sorted(
    basket?.layers ?? [],
    (layer) => layer.layer_number
  ).reduce<GroupingAccumulator>(
    (acc, layer) => {
      const groupId = layer.layer_group_id ?? null;
      const component = {
        materialName: layer.material_name,
        mass: layer.quantity.value,
      };
      const previous = acc.layers[acc.layers.length - 1];
      if (
        previous !== undefined &&
        groupId !== null &&
        groupId === acc.lastGroupId
      ) {
        const components = [...previous.components, component];
        return {
          layers: [
            ...acc.layers.slice(0, -1),
            {
              ...previous,
              materialName: components
                .map((part) => part.materialName)
                .join(" + "),
              mass: previous.mass + component.mass,
              components,
            },
          ],
          lastGroupId: groupId,
        };
      }
      return {
        layers: [
          ...acc.layers,
          {
            layerNumber: layer.layer_number,
            materialName: component.materialName,
            mass: component.mass,
            components: [component],
          },
        ],
        lastGroupId: groupId,
      };
    },
    { layers: [], lastGroupId: null }
  ).layers;

// Pair planned, reported and ScrapEye-measured layers per basket. All three now come
// through the mixes array as ordinary mix sources. Every basket present in any source
// appears once, ordered by basket number. The shared mass scale is set so the planned
// column fills PLANNED_FILL_FRACTION of the bucket (falling back to whichever source
// has mass when there is no planned layering).
export const buildLayeringComparison = (
  plannedBaskets: MixBasket[],
  reportedBaskets: MixBasket[],
  measuredBaskets: MixBasket[]
): BasketLayeringComparison[] => {
  const basketNumbers = sorted(
    Array.from(
      new Set([
        ...plannedBaskets.map((basket) => basket.basket_number),
        ...reportedBaskets.map((basket) => basket.basket_number),
        ...measuredBaskets.map((basket) => basket.basket_number),
      ])
    ),
    (basketNumber) => basketNumber
  );

  return basketNumbers.map((basketNumber) => {
    const planned = fromMixLayers(
      plannedBaskets.find((basket) => basket.basket_number === basketNumber)
    );
    const reported = fromMixLayers(
      reportedBaskets.find((basket) => basket.basket_number === basketNumber)
    );
    const measured = fromMixLayers(
      measuredBaskets.find((basket) => basket.basket_number === basketNumber)
    );
    const referenceMass =
      totalMass(planned) || totalMass(measured) || totalMass(reported);
    const scaleMass =
      referenceMass > 0 ? referenceMass / PLANNED_FILL_FRACTION : 1;
    return { basketNumber, planned, reported, measured, scaleMass };
  });
};
